'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, User, ChevronDown } from 'lucide-react';
import SongSearch from './SongSearch';
import type { Song } from '@/lib/songs';
import { ALL_SONGS, INSTRUMENTS } from '@/lib/songs';
import type { Instrument } from '@/lib/songs';

type SoloFormValues = {
  musicianName: string;
  contactEmail: string;
  instrument: Instrument | '';
  songsKnown: string;
  notes: string;
};

type Props = {
  onSuccess: (data: { musicianName: string; instrument: string; songs: Song[] }) => void;
};

export default function SoloForm({ onSuccess }: Props) {
  const [selectedSongs, setSelectedSongs] = useState<Song[]>([]);
  const [songsError, setSongsError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<SoloFormValues>({ mode: 'onBlur', defaultValues: { instrument: '' } });

  const instrumentValue = watch('instrument');

  function onSubmit(data: SoloFormValues) {
    setSongsError('');
    setLoading(true);

    fetch('/api/signups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'solo',
        musicianName: data.musicianName,
        contactEmail: data.contactEmail,
        instrument: data.instrument,
        songs: selectedSongs,
        songsKnown: data.songsKnown,
        notes: data.notes,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Sign-up failed');
        }
        return res.json();
      })
      .then(() => {
        setLoading(false);
        reset();
        setSelectedSongs([]);
        toast.success('You\'re on the list!', {
          description: selectedSongs.length > 0
            ? `${data.musicianName} (${data.instrument}) — ${selectedSongs.length} song${selectedSongs.length !== 1 ? 's' : ''} added.`
            : `${data.musicianName} (${data.instrument}) — signed up!`,
        });
        onSuccess({ musicianName: data.musicianName, instrument: data.instrument, songs: selectedSongs });
      })
      .catch((err: Error) => {
        setLoading(false);
        toast.error('Sign-up failed', { description: err.message || 'Please try again.' });
      });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      {/* Musician name */}
      <div>
        <label htmlFor="musicianName" className="label-text">
          Your Name <span className="text-[#FF1493]">*</span>
        </label>
        <p className="helper-text mb-1.5">How you&apos;d like to be listed and matched.</p>
        <input
          id="musicianName"
          type="text"
          className={`input-dark ${errors.musicianName ? 'border-[#FF1493]' : ''}`}
          placeholder="e.g. Priya Mehta"
          {...register('musicianName', {
            required: 'Your name is required.',
            minLength: { value: 2, message: 'Must be at least 2 characters.' },
          })}
        />
        {errors.musicianName && <p className="error-text">{errors.musicianName.message}</p>}
      </div>

      {/* Instrument */}
      <div>
        <label htmlFor="instrument" className="label-text">
          Primary Instrument <span className="text-[#FF1493]">*</span>
        </label>
        <p className="helper-text mb-1.5">What do you play? You&apos;ll be matched with musicians playing different instruments.</p>
        <div className="relative">
          <select
            id="instrument"
            className={`input-dark appearance-none pr-9 ${
              errors.instrument ? 'border-[#FF1493]' : ''
            } ${!instrumentValue ? 'text-[#555]' : 'text-white'}`}
            {...register('instrument', { required: 'Please select your instrument.' })}
          >
            <option value="" disabled>Select your instrument…</option>
            {INSTRUMENTS.map((inst) => (
              <option key={`inst-${inst}`} value={inst}>{inst}</option>
            ))}
          </select>
          <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] pointer-events-none" />
        </div>
        {errors.instrument && <p className="error-text">{errors.instrument.message}</p>}
      </div>

      {/* Song list */}
      <div>
        <label className="label-text">
          Songs You Know <span className="text-[#555] font-normal">(optional)</span>
        </label>
        <p className="helper-text mb-1.5">
          Search for songs from the list, or add them in the free text box below.
        </p>
        <SongSearch
          songs={ALL_SONGS}
          selected={selectedSongs}
          onSelect={(s) => { setSelectedSongs((prev) => [...prev, s]); setSongsError(''); }}
          onRemove={(id) => setSelectedSongs((prev) => prev.filter((s) => s.id !== id))}
          multi
          placeholder="Search songs you know…"
          error={songsError}
        />
        {selectedSongs.length > 0 && (
          <p className="text-[#888] text-xs mt-2 tabular-nums">
            {selectedSongs.length} song{selectedSongs.length !== 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      {/* Free text songs known */}
      <div>
        <label htmlFor="songsKnown" className="label-text">
          Songs You Know
        </label>
        <p className="helper-text mb-1.5">List songs you can play — one per line or comma-separated. The more you add, the better your match chances. Please list the artist name!</p>
        <textarea
          id="songsKnown"
          rows={4}
          className="input-dark resize-none"
          placeholder={"Oasis - Wonderwall\nNirvana - Come As You Are"}
          {...register('songsKnown')}
        />
      </div>

      {/* Contact email */}
      <div>
        <label htmlFor="soloEmail" className="label-text">
          Contact Email <span className="text-[#FF1493]">*</span>
        </label>
        <p className="helper-text mb-1.5">We&apos;ll contact you when we find a match.</p>
        <input
          id="soloEmail"
          type="email"
          className={`input-dark ${errors.contactEmail ? 'border-[#FF1493]' : ''}`}
          placeholder="you@example.co.uk"
          {...register('contactEmail', {
            required: 'Email is required.',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
          })}
        />
        {errors.contactEmail && <p className="error-text">{errors.contactEmail.message}</p>}
      </div>

      {/* Optional notes */}
      <div>
        <label htmlFor="soloNotes" className="label-text">
          Notes <span className="text-[#555] font-normal">(optional)</span>
        </label>
        <p className="helper-text mb-1.5">Anything else — skill level, availability, instruments you can double on.</p>
        <textarea
          id="soloNotes"
          rows={3}
          className="input-dark resize-none"
          placeholder="e.g. Beginner-friendly preferred, can also play ukulele…"
          {...register('notes')}
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="btn-secondary w-full flex items-center justify-center gap-2 py-3.5 text-base"
        style={{ minHeight: '52px' }}
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            <span>Signing up…</span>
          </>
        ) : (
          <>
            <User size={18} />
            <span>Add Me to the List</span>
          </>
        )}
      </button>
    </form>
  );
}